import {
  Avatar,
  Box,
  Center,
  Divider,
  Flex,
  Grid,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { ReactElement, useEffect, useState } from 'react'
import { TbChevronLeft, TbChevronRight, TbDots } from 'react-icons/tb'
import { getSkillLevelDisplay } from '../../functions/Capitalize'
import useElementWidth from '../../hooks/useElementWidth'
import { formatDuration } from '../../pages/host/EventsEvents'
import EventDetailCard from '../cards/EventDetailCard'
import { CalendarEdit } from '../icons/icons'

// const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']
const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const daysOfWeekMobile = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function formatTimeRange(start: Date, end: Date): string {
  const startTime = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
  const endTime = end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })

  const timeRange = `${startTime} - ${endTime}`

  return `${timeRange}`
}

function formatTimeRangeShortened(start: Date, end: Date): string {
  const startTime = start.getHours()

  const endTime = end.getHours()

  return `${startTime > 12 ? startTime - 12 : startTime}${startTime > 12 ? 'PM' : 'AM'} - ${endTime > 12 ? endTime - 12 : endTime
    }${endTime > 12 ? 'PM' : 'AM'}`
}

export const makeDate = (year: number, month: number, day: number) => {
  return new Date(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
}

export const getEventsOfTheDay = (day: number, month: number, year: number, allEvents: any) => {
  console.log(allEvents)

  if (!allEvents?.events) return []

  let eventsOfTheDay = allEvents?.events?.filter((event: any) => {
    const date = new Date(event.start_time)
    return date.getDate() === day && date.getMonth() === month && date.getFullYear() === year
  })

  return eventsOfTheDay || []
}

function convertTimeTo12HourFormat(timeString: string) {
  const [hours, minutes] = timeString.split(':')
  let hour = parseInt(hours, 10)
  const minute = parseInt(minutes, 10)

  // Determine if it's AM or PM
  const period = hour >= 12 ? 'PM' : 'AM'

  // Convert to 12-hour format
  if (hour > 12) {
    hour -= 12
  } else if (hour === 0) {
    hour = 12
  }

  // Add leading zero if necessary
  //const formattedHour = hour < 10 ? `0${hour}` : `${hour}`;

  return `${hour}:${minute < 10 ? '0' : ''}${minute} ${period}`
}

const CalendarLarge = ({
  month,
  year,
  setMonth,
  setYear,
  topElements,
  customRender,
  events,
  availability,
  onExtraOptionsOpen,
  onShowEventsOfTheDay,
  onOpenModal,
  setDate,
  isLoading,
}: {
  month: number
  year: number
  setMonth: React.Dispatch<React.SetStateAction<number>>
  setYear: React.Dispatch<React.SetStateAction<number>>

  setDate?: (v: Date) => void

  topElements?: ReactElement
  customRender?: ReactElement

  events?: any
  availability?: any
  onOpenModal?: () => void
  isLoading?: boolean

  onExtraOptionsOpen?: (v: string) => void
  onShowEventsOfTheDay?: (x: number, y: number, z: number) => void
}) => {

  const [calendarWidth, calendarRef] = useElementWidth<HTMLDivElement>()
  const isMobile = calendarWidth ? calendarWidth < 600 : false
  const isTablet = calendarWidth ? calendarWidth < 1040 : false

  //const [showEvent , setShowEvent] = useState(false)
  const [eventToShow, setEventToShow] = useState<any>(null)

  const onPreviousMonth = () => {
    if (month === 0) {
      setYear(year - 1)
      setMonth(11)
    } else {
      setMonth(month - 1)
    }
  }
  const onNextMonth = () => {
    if (month === 11) {
      setYear(year + 1)
      setMonth(0)
    } else {
      setMonth(month + 1)
    }
  }

  useEffect(() => { }, [month, year])

  const getMonthName = (month: number) => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    return monthNames[month]
  }

  const getDaysInMonth = (month: number, year: number) => {
    // #region agent log
    const days = new Date(year, month + 1, 0).getDate()
    // #endregion
    return days
  }

  const getAvailabilityOfTheDay = (day: number, month: number, year: number) => {
    if (!availability) return []

    let availabilityOfTheDay =
      availability[`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`]

    return availabilityOfTheDay || []
  }

  const renderCalendar = (month: number, year: number) => {
    const daysInMonth = getDaysInMonth(month, year)
    const firstDayOfMonth = new Date(year, month, 1)
    const firstDayWeekday = firstDayOfMonth.getDay()
    // Sunday-first layout: use getDay() as spacer count (0 = Sun, 1 = Mon, ...) so 1st aligns with header
    const startSpacerArray = Array.from({ length: firstDayWeekday })
    const endSpacerLen = 7 - ((startSpacerArray.length + daysInMonth) % 7)
    const endSpacerArray = Array.from({ length: endSpacerLen === 7 ? 0 : endSpacerLen })


    const calendarDays = []

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i)
      const dayOfWeek = date.getDay()
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
      const isLastRow =
        Math.ceil((startSpacerArray.length + daysInMonth) / 7) === Math.ceil((startSpacerArray.length + i) / 7)
      const isLastItemOnRow = (startSpacerArray.length + i) % 7 === 0

      const currentDate = new Date()
      const currentMonth = currentDate.getMonth()
      const currentYear = currentDate.getFullYear()
      const isPastDate =
        currentYear > year ||
        (currentYear === year && currentMonth > month) ||
        (currentYear === year && currentMonth === month && i < currentDate.getDate())

      calendarDays.push(
        isMobile ? (
          <Center
            key={i}
            h="66px"
            borderTopColor="#DCE2EA"
            borderTopWidth="1px"
            cursor="pointer"
            position="relative"
            onClick={() => {
              if (onShowEventsOfTheDay && getEventsOfTheDay(i, month, year, events).length > 0)
                onShowEventsOfTheDay(i, month, year)
              if (setDate) setDate(makeDate(year, month, i))
              if (onOpenModal) {
                onOpenModal()
              }
            }}
          >
            <Box h="50px">
              <Text fontSize="lg" color={isWeekend || isPastDate ? '#7E8B9A' : '#171C22'}>
                {i}
              </Text>
            </Box>
            {getEventsOfTheDay(i, month, year, events).length > 0 && (
              <Box bgColor="#06C270" w="7.5px" h="7.5px" borderRadius="50%" position="absolute" bottom="20px" />
            )}
          </Center>
        ) : (
          <Box
            key={i}
            h="110px"
            cursor="pointer"
            borderBottomWidth={isLastRow ? '0' : '1px'}
            borderColor="#DCE2EA"
            p="10px"
            borderRightWidth={isLastItemOnRow ? '0' : '1px'}
            bg={isWeekend || isPastDate ? '#F5F7F9' : 'white'}
            position="relative"
            onClick={() => {
              if (setDate) setDate(makeDate(year, month, i))
              if (onOpenModal && makeDate(year, month, i) >= new Date()) onOpenModal()
            }}
          >
            {onOpenModal && makeDate(year, month, i) >= new Date() && (
              <Icon w="24px" h="24px" position="absolute" top="10px" right="10px">
                <CalendarEdit />
              </Icon>
            )}
            <Text fontSize="sm" color="#171C22">
              {i}
            </Text>

            <Box alignItems="center">
              {getAvailabilityOfTheDay(i, month, year)
                .slice(0, 2)
                .map((available_time: any) => {
                  const getParsedTime = (time: string) => {
                    const [hours, minutes] = time.split(':')
                    const numHours = parseInt(hours, 10)
                    const numMinutes = parseInt(minutes, 10)

                    return (
                      `${numHours > 12 ? numHours - 12 : numHours}${numHours > 12 ? 'PM' : 'AM'}` +
                      ':' +
                      `${numMinutes < 10 ? '0' : ''}${numMinutes}`
                    )
                  }

                  return (
                    <Center mb="8px" gap="4px">
                      <Text color="#171C22" fontSize="sm">
                        {isTablet
                          ? getParsedTime(available_time[0]) + ' - ' + getParsedTime(available_time[1])
                          : `${convertTimeTo12HourFormat(available_time[0])} - ${convertTimeTo12HourFormat(
                            available_time[1]
                          )}`}
                      </Text>
                    </Center>
                  )
                })}
            </Box>
            {getAvailabilityOfTheDay(i, month, year).length > 2 && (
              <Popover placement={dayOfWeek < 4 ? 'right' : 'left'} isLazy>
                <PopoverTrigger>
                  <Text
                    position="absolute"
                    bottom="4px"
                    left="8px"
                    color="#171C22"
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    +{getAvailabilityOfTheDay(i, month, year).length - 2}
                  </Text>
                </PopoverTrigger>
                <PopoverContent w="auto" minW="200px" onClick={(e) => e.stopPropagation()}>
                  <PopoverBody>
                    <Text fontWeight="600" mb="8px" fontSize="sm">
                      All Availability ({i} {getMonthName(month).slice(0, 3)})
                    </Text>
                    {getAvailabilityOfTheDay(i, month, year).map((available_time: any, idx: number) => {
                      const getParsedTime = (time: string) => {
                        const [hours, minutes] = time.split(':')
                        const numHours = parseInt(hours, 10)
                        const numMinutes = parseInt(minutes, 10)
                        return (
                          `${numHours > 12 ? numHours - 12 : numHours}${numHours >= 12 ? 'PM' : 'AM'}` +
                          ':' +
                          `${numMinutes < 10 ? '0' : ''}${numMinutes}`
                        )
                      }
                      return (
                        <Text key={idx} color="#171C22" fontSize="sm" mb="4px">
                          {getParsedTime(available_time[0])} - {getParsedTime(available_time[1])}
                        </Text>
                      )
                    })}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}

            <Box alignItems="center">
              {getEventsOfTheDay(i, month, year, events)
                .slice(0, 2)
                .map((event: any) => {
                  return (
                    <Box position="relative">
                      <Center
                        mb="8px"
                        gap="4px"
                        onClick={() => {
                          if (event.id === eventToShow?.id) {
                            //setShowEvent(false)
                            setEventToShow(null)
                            return
                          }
                          //setShowEvent(true)
                          setEventToShow(event)
                        }}
                      >
                        <Avatar w="14px" h="14px" src={event.venue.images.length > 0 ? event.venue.images[0] : ''} />
                        <Text color="#171C22" fontSize="sm">
                          {isTablet
                            ? formatTimeRangeShortened(new Date(event.start_time), new Date(event.end_time))
                            : formatTimeRange(new Date(event.start_time), new Date(event.end_time))}
                        </Text>
                      </Center>
                      {event.id === eventToShow?.id && (
                        <Box
                          position="absolute"
                          zIndex={100}
                          w="550px"
                          left={dayOfWeek < 4 ? '0' : undefined}
                          right={dayOfWeek >= 4 ? '0' : undefined}
                        >
                          <EventDetailCard
                            id={eventToShow.id}
                            status={'hosted'}
                            time={new Date(eventToShow.start_time)}
                            type={eventToShow.type}
                            court={eventToShow.court}
                            level={getSkillLevelDisplay(eventToShow.skill_level)}
                            host={eventToShow.host}
                            title={eventToShow.title}
                            coaches={eventToShow.coaches}
                            players={eventToShow.participants}
                            description={eventToShow?.description ?? ''}
                            price={eventToShow.fee}
                            duration={formatDuration(new Date(eventToShow.start_time), new Date(eventToShow.end_time))}
                            onOpenWaitlist={() => { }}
                            thumbnail={eventToShow.venue.images.length > 0 ? eventToShow.venue.images[0] : ''}
                            location={eventToShow.venue.address}
                            locationName={eventToShow.venue.name}
                            //waitlistPosition={row.status?.position}
                            onExtraOptionsOpen={onExtraOptionsOpen}
                          />
                        </Box>
                      )}
                    </Box>
                  )
                })}
            </Box>

            {getEventsOfTheDay(i, month, year, events).length > 2 && (
              <Popover placement={dayOfWeek < 4 ? 'right' : 'left'} isLazy>
                <PopoverTrigger>
                  <Text
                    position="absolute"
                    bottom="4px"
                    left="8px"
                    color="#171C22"
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (onShowEventsOfTheDay) onShowEventsOfTheDay(i, month, year)
                    }}
                  >
                    +{getEventsOfTheDay(i, month, year, events).length - 2}
                  </Text>
                </PopoverTrigger>
                <PopoverContent
                  w="auto"
                  minW="300px"
                  maxW="500px"
                  maxH="400px"
                  overflowY="auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <PopoverBody>
                    <Text fontWeight="600" mb="12px" fontSize="sm">
                      All Events ({i} {getMonthName(month).slice(0, 3)})
                    </Text>
                    {getEventsOfTheDay(i, month, year, events).map((event: any, idx: number) => {
                      return (
                        <Box
                          key={idx}
                          mb="12px"
                          pb="12px"
                          borderBottom={
                            idx < getEventsOfTheDay(i, month, year, events).length - 1 ? '1px solid #DCE2EA' : 'none'
                          }
                        >
                          <Flex gap="8px" alignItems="center" justifyContent="space-between" mb="4px">
                            <Flex gap="8px" alignItems="center" flex="1">
                              <Avatar
                                w="20px"
                                h="20px"
                                src={event.venue.images.length > 0 ? event.venue.images[0] : ''}
                              />
                              <Text color="#171C22" fontSize="sm" fontWeight="500">
                                {event.title || event.type}
                              </Text>
                            </Flex>
                            {onExtraOptionsOpen && (
                              <IconButton
                                aria-label="options"
                                icon={<Icon w="18px" h="18px" as={TbDots} />}
                                color="#171C22"
                                variant="unstyled"
                                size="sm"
                                minW="auto"
                                h="auto"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (onExtraOptionsOpen) onExtraOptionsOpen(event.id)
                                }}
                              />
                            )}
                          </Flex>
                          <Text color="#7E8B9A" fontSize="xs" ml="28px">
                            {isTablet
                              ? formatTimeRangeShortened(new Date(event.start_time), new Date(event.end_time))
                              : formatTimeRange(new Date(event.start_time), new Date(event.end_time))}
                          </Text>
                          {event.venue?.name && (
                            <Text color="#7E8B9A" fontSize="xs" ml="28px">
                              {event.venue.name}
                            </Text>
                          )}
                        </Box>
                      )
                    })}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}
          </Box>
        )
      )
    }

    return (
      <Box>
        <Grid templateColumns="repeat(7, 1fr)">
          {startSpacerArray.map((_, index) =>
            isMobile ? (
              <Box key={`blank-${index}`} h="66px" />
            ) : (
              <Box
                key={`blank-${index}`}
                h="110px"
                borderColor="#DCE2EA"
                bg="#F5F7F9"
                borderRightWidth={index === startSpacerArray.length ? '0' : '1px'}
                borderBottomWidth="1px"
              />
            )
          )}
          {calendarDays}
          {endSpacerArray.map((_, index) =>
            isMobile ? (
              <Box key={`blank-${index}`} h="66px" />
            ) : (
              <Box
                key={`blank-${index}`}
                h="110px"
                borderColor="#DCE2EA"
                bg="#F5F7F9"
                borderRightWidth={index === endSpacerArray.length - 1 ? '0' : '1px'}
              />
            )
          )}
        </Grid>
      </Box>
    )
  }

  if (customRender)
    return (
      <Box
        w="100%"
        ref={calendarRef}
        border="1px solid #DCE2EA"
        borderRadius="10px"
        bg="white"
        boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
        p={isMobile ? '15px' : '20px'}
      >
        {topElements && (
          <>
            <Flex p="5px" gap="12px" justifyContent="left" alignItems="center" mb="15px">
              {topElements}
            </Flex>
            <Divider />
          </>
        )}
        {customRender}
      </Box>
    )

  return (
    <>
      {/* {showEvent && <Center position="absolute" w="100%" bottom="-240px" zIndex={1}>
    <EventDetailCard 
        id={eventToShow.id}
        status={"hosted"}
        time={new Date(eventToShow.start_time)}
        type={eventToShow.type}
        court={eventToShow.court}
        level={getSkillLevelDisplay(eventToShow.skill_level)}
        host={eventToShow.host}
        coaches={eventToShow.coaches}
        players={eventToShow.participants}
        description={eventToShow?.description ?? ""}
        price={eventToShow.fee}
        duration={eventToShow.duration}
        onOpenWaitlist={() => {}}

        thumbnail={eventToShow.venue.images.length > 0 ? eventToShow.venue.images[0] : ""}
        location={eventToShow.venue.address}
        locationName={eventToShow.venue.name}

        //waitlistPosition={row.status?.position}
        onExtraOptionsOpen={onExtraOptionsOpen}
    />    </Center>} */}
      <Box
        w="100%"
        ref={calendarRef}
        border={isMobile ? undefined : '1px solid #DCE2EA'}
        borderRadius="10px"
        bg="white"
        boxShadow={isMobile ? undefined : '0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14'}
      >
        {topElements && (
          <>
            <Flex p={isMobile ? '0px' : '20px'} gap="12px" justifyContent="left" alignItems="center">
              {topElements}
            </Flex>
            {!isMobile && <Divider />}
          </>
        )}
        <Flex
          mt={isMobile ? '20px' : '0px'}
          p={isMobile ? '0' : '20px'}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Text variant="title" fontSize="md">
              {getMonthName(month)} {year}
            </Text>
            <Text variant="subtle" fontSize="sm" mt={isMobile ? 0 : '10px'}>
              Track all of your events
            </Text>
          </Box>

          <Flex gap="20px">
            <IconButton
              variant="outline"
              aria-label="previous month"
              icon={<Icon w="18px" h="18px" as={TbChevronLeft} />}
              onClick={() => onPreviousMonth()}
            />
            <IconButton
              variant="outline"
              aria-label="next month"
              icon={<Icon w="18px" h="18px" as={TbChevronRight} />}
              onClick={() => onNextMonth()}
            />
          </Flex>
        </Flex>
        {isMobile ? <></> : <Divider />}
        <Flex h="42px" alignItems={isMobile ? 'end' : 'center'}>
          {(isMobile ? daysOfWeekMobile : daysOfWeek).map((day) => (
            <Flex key={day} flex="1" justifyContent="center">
              <Text
                variant="title"
                fontSize={isMobile ? '10px' : 'sm'}
                lineHeight={isMobile ? '10px' : undefined}
                mb={isMobile ? '5px' : 0}
                fontWeight={isMobile ? '600' : '500'}
              >
                {day.toUpperCase()}
              </Text>
            </Flex>
          ))}
        </Flex>
        <Divider mb={isMobile ? '19px' : 0} />
        {isLoading ? (
          <Grid templateColumns="repeat(7, 1fr)">
            {Array.from({ length: 35 }).map((_, index) => (
              <Box key={index}>
                {isMobile ? (
                  <Center h="66px" borderTopColor="#DCE2EA" borderTopWidth="1px">
                    <Skeleton h="40px" w="40px" borderRadius="md" />
                  </Center>
                ) : (
                  <Box
                    h="110px"
                    borderBottomWidth="1px"
                    borderRightWidth={index % 7 === 6 ? '0' : '1px'}
                    borderColor="#DCE2EA"
                    p="10px"
                  >
                    <Skeleton h="20px" w="30px" mb="10px" />
                    <Skeleton h="14px" w="80%" mb="6px" />
                    <Skeleton h="14px" w="60%" />
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
        ) : (
          renderCalendar(month, year)
        )}
      </Box>
    </>
  )
}

export default CalendarLarge
